import React, {ReactNode} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faImage, faLocationDot, faStar, faXmark} from "@fortawesome/free-solid-svg-icons";
import {Link} from "react-router-dom";
import {getMacaulayAssetUrl} from "../helpers/macaulayLibrary";
import './observation-card.scss';

const DEFAULT_BIRD_ICON = '/assets/fallback-bird.svg';

export type ObservationCardLink = {
    label: string,
    to: string
}

export type ObservationCardProps = {
    variant?: 'observation' | 'location-detail',
    kicker?: string|ReactNode,
    title?: string|ReactNode,
    copy?: string|ReactNode,
    detail?: ReactNode,
    location?: string|ReactNode,
    image?: string
    badges?: string[],
    familyLink?: ObservationCardLink,
    sourceUrl?: string,
    sourceLabel?: string,
    macaulayCatalogNumbers?: string[],
    onClose?: () => void,
    closeLabel?: string
}

const getBadgeClassName = (badge: string) => {
    const modifier = badge.toLowerCase().replace(/\s+/g, '-');
    return `observation-card__badge observation-card__badge--${modifier}`;
}

export function ObservationCard({
    variant = 'observation',
    kicker,
    title,
    copy,
    detail,
    location,
    image,
    badges = [],
    familyLink,
    sourceUrl,
    sourceLabel,
    macaulayCatalogNumbers = [],
    onClose,
    closeLabel = 'Close'
}: ObservationCardProps) {
    const isFallbackImage = !image || image.endsWith(DEFAULT_BIRD_ICON);
    const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
        if (event.currentTarget.src.endsWith(DEFAULT_BIRD_ICON)) return;

        event.currentTarget.src = DEFAULT_BIRD_ICON;
        event.currentTarget.classList.add('observation-card__img-element--fallback');
    }

    const renderBadgeContent = (badge: string) => (
        <>
            {badge.toLowerCase() === 'lifer' ?
                <FontAwesomeIcon icon={faStar} aria-hidden="true" />
                : null
            }
            {badge}
        </>
    );

    return (
        <div className={`observation-card ${variant === 'location-detail' ? 'observation-card--location-detail' : ''}`}>
            {onClose ?
                <button
                    className="observation-card__close"
                    type="button"
                    aria-label={closeLabel}
                    onClick={onClose}
                >
                    <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                </button>
                : null
            }
            <div className="observation-card__img">
                <img
                    src={image || DEFAULT_BIRD_ICON}
                    alt={typeof title === 'string' ? title : ''}
                    className={isFallbackImage ? 'observation-card__img-element observation-card__img-element--fallback' : 'observation-card__img-element'}
                    onError={handleImageError}
                />
                <div className="observation-card__image-content">
                    {sourceUrl ?
                        <a className="observation-card__source-link" href={sourceUrl} target="_blank" rel="noreferrer">{sourceLabel}</a>
                        : null
                    }
                </div>
            </div>
            {(kicker || title || copy || detail || location || badges.length) ?
                <div className={`observation-card__body ${variant === 'location-detail' ? 'observation-card__body--location-detail' : ''}`}>
                    {kicker ?
                        <div className="observation-card__kicker">{kicker}</div>
                        : null
                    }
                    {familyLink ?
                        <Link className="observation-card__family-link" to={familyLink.to}>
                            {familyLink.label}
                        </Link>
                        : null
                    }
                    {title ?
                        <div className="observation-card__title">{title}</div>
                        : null
                    }
                    {variant === 'location-detail' ?
                        <>
                            {location ?
                                <div className="observation-card__location">
                                    <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                                    <span>{location}</span>
                                </div>
                                : null
                            }
                            {badges.length ?
                                <div className="observation-card__detail-badges" aria-label="Observation highlights">
                                    {badges.map(badge => (
                                        <span className={getBadgeClassName(badge)} key={badge}>
                                            {renderBadgeContent(badge)}
                                        </span>
                                    ))}
                                </div>
                                : null
                            }
                            {detail ?
                                <div className="observation-card__detail">
                                    {detail}
                                </div>
                                : null
                            }
                        </>
                        :
                        <>
                            {copy ?
                                <div className="observation-card__copy">{copy}</div>
                                : null
                            }
                            {macaulayCatalogNumbers.length ?
                                <section className="observation-card__photos" aria-label="Photos">
                                    <h3>Photos</h3>
                                    <ul>
                                        {macaulayCatalogNumbers.map((catalogNumber, index) => (
                                            <li key={`${catalogNumber}-${index}`}>
                                                <a
                                                    href={getMacaulayAssetUrl(catalogNumber)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <FontAwesomeIcon icon={faImage} aria-hidden="true" />
                                                    <span>ML{catalogNumber}</span>
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                                : null
                            }
                        </>
                    }
                </div> : null
            }
            {variant !== 'location-detail' && badges.length ?
                <div className="observation-card__badges" aria-label="Observation highlights">
                    {badges.map(badge => (
                        <span className={getBadgeClassName(badge)} key={badge}>
                            {renderBadgeContent(badge)}
                        </span>
                    ))}
                </div>
                : null
            }
        </div>
    )
}
